import axios from 'axios';
import { store } from '../store';
import { setJourneySessionId } from '../actions/component_actions';

// API endpoint for journey tracking
const TRACKING_ENDPOINT = `${process.env.EVENTVIEWER_SERVER}/api/v1/tracking/journey-action`;

/**
 * Get session ID from Redux store or create a new one
 * @returns {string} Session ID
 */
const getSessionId = () => {
  const state = store.getState();
  let sessionId = state.component.get('journeySessionId');

  // If no session ID exists, create a new one and store it in Redux
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    store.dispatch(setJourneySessionId(sessionId));
  }

  return sessionId;
};

/**
 * Get account and site IDs from Redux store
 * @returns {Object} Object containing account_id and site_id
 */
const getContextIds = () => {
  const state = store.getState();
  const settings = state.component.get('settings');
  return {
    account_id: settings.get('accountId'),
    site_id: settings.getIn(['site', 'name']),
  };
};

/**
 * Get tracking ID from URL query parameters
 * @returns {string|null} Tracking ID if present
 */
const getTrackingId = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('ts_tracking_id');
};

/**
 * Track a journey action
 * @param {string} actionType - Type of action
 * @param {Object} data - Additional data for the action
 * @returns {Promise} Axios promise
 */
const trackJourneyAction = async (actionType, data = {}) => {
  try {
    const sessionId = getSessionId();
    const { account_id, site_id } = getContextIds();
    const trackingId = getTrackingId();

    // Prepare tracking payload
    const payload = {
      session_id: sessionId,
      account_id,
      site_id,
      action_type: actionType,
      ...data,
    };

    // Add tracking_id if present
    if (trackingId) {
      payload.tracking_id = trackingId;
    }

    // Send tracking data to API
    return await axios.post(TRACKING_ENDPOINT, payload);
  } catch (error) {
    console.error('Journey tracking error:', error);
    // Silent failure - don't disrupt user experience
    return null;
  }
};

/**
 * Track when the widget is loaded/viewed
 * @returns {Promise} Tracking promise
 */
export const trackWidgetView = () => trackJourneyAction('widget_view', {});

/**
 * Track when a user views an event
 * @param {Object} event - Event object
 * @returns {Promise} Tracking promise
 */
export const trackEventView = (event) =>
  trackJourneyAction('event_view', {
    event_id: event.get('id'),
  });

/**
 * Track when a user selects a date for an event
 * @param {Object} eventTime - Event time object
 * @returns {Promise} Tracking promise
 */
export const trackDateSelection = (eventTime) =>
  trackJourneyAction('date_select', {
    event_id: eventTime.get('id'),
  });

/**
 * Track when a user enters the ticket page
 * @param {Object} event - Event object
 * @returns {Promise} Tracking promise
 */
export const trackTicketPage = (event) =>
  trackJourneyAction('tickets_selection', {
    event_id: event.get('id'),
  });

/**
 * Track when a user selects tickets
 * @param {string} ticketId - Ticket ID
 * @param {Object} event - Event object
 * @param {number} quantity - Quantity selected
 * @returns {Promise} Tracking promise
 */
export const trackTicketSelection = (ticketId, event, quantity) =>
  trackJourneyAction('ticket_select', {
    event_id: event.get('id'),
    ticket_id: ticketId,
    action_data: {
      quantity,
    },
  });

/**
 * Track when a customer enters their information
 * @param {Object} event - Event object
 * @param {Object} customerInfo - Customer information
 * @returns {Promise} Tracking promise
 */
export const trackCustomerInfo = (event, customerInfo) => {
  const { first_name, last_name, email } = customerInfo;

  return trackJourneyAction('customer_info', {
    event_id: event.get('id'),
    action_data: {
      attendee_first_name: first_name,
      attendee_last_name: last_name,
      attendee_email: email,
    },
  });
};

/**
 * Track when a user attempts payment
 * @param {Object} event - Event object
 * @param {Object} cart - Cart object
 * @returns {Promise} Tracking promise
 */
export const trackPaymentAttempt = (event, cart) =>
  trackJourneyAction('payment_attempt', {
    event_id: event.get('id'),
    cart_id: cart.cart_id,
  });

/**
 * Track payment result (success or failure)
 * @param {Object} event - Event object
 * @param {boolean} success - Whether payment was successful
 * @param {Object} data - Additional payment data
 * @returns {Promise} Tracking promise
 */
export const trackPaymentResult = (event, success, data = {}) =>
  trackJourneyAction(success ? 'payment_success' : 'payment_failure', {
    event_id: event.get('id'),
    ...data,
  });
